<script setup>
import {computed, ref, watch} from 'vue'

const props = defineProps({
  trigger: {type: Object, default: () => ({})},
  // Vue 2.7's bare v-model compiles to value/input, not Vue 3's
  // modelValue/update:modelValue - every call site here uses plain v-model.
  value: {type: String, default: '_default'},
})
const emit = defineEmits(['input'])

// checkbox-selectable presets (ANDed together) + one optional free-form custom
// expression, also ANDed in. Presets are canned per-trigger (config/notification_triggers.php).
// A preset with a `group` is mutually exclusive with its group-mates (e.g. a
// status-change event has exactly one new status) and renders as a radio
// instead of an independent checkbox; ungrouped presets stay as checkboxes.
const checkedPresets = ref([])
const radioSelections = ref({}) // group name -> selected preset's condition string, or unset
const customCondition = ref('')
const showCustom = ref(false) // custom condition starts hidden behind an "add" link

const ungroupedPresets = computed(() => (props.trigger.presets || []).filter(p => !p.group))
const groupedPresets = computed(() => {
  const groups = []
  for (const p of (props.trigger.presets || [])) {
    if (!p.group) continue
    let g = groups.find(g => g.group === p.group)
    if (!g) groups.push(g = {group: p.group, options: []})
    g.options.push(p)
  }
  return groups
})

function clearGroup(group) {
  const copy = {...radioSelections.value}
  delete copy[group]
  radioSelections.value = copy
}
function selectGroup(group, condition) {
  radioSelections.value = {...radioSelections.value, [group]: condition}
}

// triggers with a plain `tag` string var (currently just tag.added) get a
// friendly "only for this tag" input instead of requiring the raw
// `tag == "..."` expression typed into the custom-condition box. varMeta()
// serializes each var as {name,type,model,label}, not a bare type string.
const hasTagFilter = computed(() => (props.trigger.vars || {}).tag?.type === 'string')
const tagFilter = ref('')
const tagClauseRe = /^tag\s*==\s*"([^"]*)"$/

// fields a trigger declares with a fixed set of values (Trigger `choices`, e.g.
// a new quote's origination): checkboxes, built into one `path in ["A","B"]`
// clause - any ticked value matches. Nothing ticked = no limit.
const choices = computed(() => props.trigger.choices || [])
const choiceValues = ref({}) // path -> [values]
const escapeRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
function matchChoice(bare) {
  for (const c of choices.value) {
    const p = escapeRe(c.path)
    let m = bare.match(new RegExp(`^${p}\\s+in\\s+\\[(.*)\\]$`))
    if (m) return {path: c.path, values: [...m[1].matchAll(/"([^"]*)"/g)].map(x => x[1])}
    m = bare.match(new RegExp(`^${p}\\s*==\\s*"([^"]*)"$`))
    if (m) return {path: c.path, values: [m[1]]}
  }
  return null
}

function parse(condition) {
  checkedPresets.value = []
  radioSelections.value = {}
  customCondition.value = ''
  showCustom.value = false
  tagFilter.value = ''
  choiceValues.value = {}
  if (!condition || condition === '_default') return

  const presets = props.trigger.presets || []
  const leftover = []
  for (const part of String(condition).split('&&').map(s => s.trim()).filter(Boolean)) {
    const bare = part.replace(/^\((.*)\)$/, '$1').trim()
    const preset = presets.find(p => p.condition.trim() === bare || p.condition.trim() === part)
    const tagMatch = hasTagFilter.value && bare.match(tagClauseRe)
    const choice = !preset && matchChoice(bare)
    if (choice) choiceValues.value = {...choiceValues.value, [choice.path]: choice.values}
    else if (preset && preset.group) selectGroup(preset.group, preset.condition)
    else if (preset) checkedPresets.value.push(preset.condition)
    else if (tagMatch) tagFilter.value = tagMatch[1]
    else leftover.push(bare) // strip the wrapping parens - re-added below
  }
  customCondition.value = leftover.join(' && ')
  // leftover custom text means the editor opens already showing it, checked-in
  if (customCondition.value) showCustom.value = true
}
watch(() => [props.value, props.trigger], () => parse(props.value), {immediate: true})

function emitBuilt() {
  const parts = [...checkedPresets.value, ...Object.values(radioSelections.value)]
  if (tagFilter.value.trim()) parts.push(`tag == "${tagFilter.value.trim().replace(/"/g, '\\"')}"`)
  for (const c of choices.value) {
    const vals = choiceValues.value[c.path] || []
    if (vals.length) parts.push(`${c.path} in [${vals.map(v => `"${v}"`).join(',')}]`)
  }
  if (customCondition.value.trim()) parts.push(customCondition.value.trim())
  emit('input', parts.length ? parts.map(p => `(${p})`).join(' && ') : '_default')
}
watch(checkedPresets, emitBuilt, {deep: true})
watch(radioSelections, emitBuilt, {deep: true})
watch(customCondition, emitBuilt)
watch(tagFilter, emitBuilt)
watch(choiceValues, emitBuilt, {deep: true})
function setChoice(path, values) {
  choiceValues.value = {...choiceValues.value, [path]: values}
}

function addCustom() {
  showCustom.value = true
}
function removeCustom() {
  showCustom.value = false
  customCondition.value = ''
}
</script>

<template>
  <div class="w-100">
    <b-form-checkbox-group v-if="ungroupedPresets.length" v-model="checkedPresets" stacked class="mb-2">
      <b-form-checkbox v-for="p in ungroupedPresets" :key="p.condition" :value="p.condition">
        {{ p.label }}
      </b-form-checkbox>
    </b-form-checkbox-group>

    <!-- mutually exclusive presets (e.g. one status per change) - radios, with
         a Clear link since a plain radio group can't be un-set by clicking it again -->
    <div v-for="g in groupedPresets" :key="g.group" class="d-flex align-items-start mb-2">
      <b-form-radio-group
        :checked="radioSelections[g.group]"
        @change="v => selectGroup(g.group, v)"
        stacked
        class="flex-fill">
        <b-form-radio v-for="p in g.options" :key="p.condition" :value="p.condition">
          {{ p.label }}
        </b-form-radio>
      </b-form-radio-group>
      <b-button v-if="radioSelections[g.group]" variant="link" size="sm" class="text-muted p-0 ml-2"
                @click="clearGroup(g.group)">Clear</b-button>
    </div>

    <div v-if="hasTagFilter" class="mb-2">
      <label class="small text-muted mb-1 d-block">Only for this tag</label>
      <b-form-input v-model="tagFilter" size="sm" placeholder="e.g. URGENT — blank matches any tag"
                    style="max-width: 260px"/>
    </div>

    <div v-for="c in choices" :key="c.path" class="mb-2">
      <label class="small text-muted mb-1 d-block">Only when {{ c.label.toLowerCase() }} is <span class="font-italic">(any ticked; none = all)</span></label>
      <b-form-checkbox-group :checked="choiceValues[c.path] || []" @change="v => setChoice(c.path, v)">
        <b-form-checkbox v-for="o in c.options" :key="o.value" :value="o.value">{{ o.text }}</b-form-checkbox>
      </b-form-checkbox-group>
    </div>

    <div v-if="!checkedPresets.length && !Object.keys(radioSelections).length && !customCondition.trim() && !tagFilter.trim() && !Object.values(choiceValues).some(v => v.length)"
         class="text-muted small mb-2">
      Fires on every <strong>{{ (trigger.label || 'event').toLowerCase() }}</strong>.
    </div>

    <b-link v-if="!showCustom" class="small" @click="addCustom">
      <font-awesome-icon :icon="['fas', 'plus']"/> Add a custom condition
    </b-link>
    <template v-else>
      <b-form-textarea v-model="customCondition" rows="2" size="sm" class="font-monospace"
                       placeholder='e.g. to == "P" and order.channel != "EBAY"'/>
      <div class="d-flex align-items-start justify-content-between mt-1">
        <div class="text-muted small">
          Vars:
          <code v-for="(m, k) in (trigger.vars || {})" :key="k" class="mr-1">{{ k }}</code>
          <code>myself</code>
        </div>
        <b-button variant="link" size="sm" class="text-danger p-0 ml-2" @click="removeCustom">Remove</b-button>
      </div>
    </template>
  </div>
</template>
